import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, KeyRound, Layers, MapPin, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useDepot } from "@/context/DepotContext";

const ALL = "all";
const EMPTY = { name: "", email: "", password: "", access: "" };

const AccessSelect = ({ value, onChange, disabled, testId }) => {
  const { depots } = useDepot();
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Choose access" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All depots (company login)</SelectItem>
        {depots.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} only</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const LoginDialog = ({ login, onClose, onSaved }) => {
  const isNew = !login?.id;
  const [form, setForm] = useState(isNew ? EMPTY : { name: login.name, email: login.email, password: "", access: login.depot_id || ALL });
  const [saving, setSaving] = useState(false);
  const locked = !isNew && (login.is_you || login.is_founder);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || (isNew && (!form.email.trim() || form.password.length < 6 || !form.access))) {
      toast.error(isNew ? "Add a name, email, access and a password of 6+ characters" : "Add a name"); return;
    }
    if (form.password && form.password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    setSaving(true);
    try {
      if (isNew) await api.createLogin({ name: form.name.trim(), email: form.email.trim(), password: form.password, depot_id: form.access === ALL ? null : form.access });
      else await api.updateLogin(login.id, {
        name: form.name.trim(), ...(form.password ? { password: form.password } : {}),
        ...(locked ? {} : form.access === ALL ? { all_depots: true } : { depot_id: form.access }),
      });
      toast.success(isNew ? "Login created — share the email and password with them" : "Login updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save login"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isNew ? "Add supervisor login" : "Edit login"}</DialogTitle>
          <DialogDescription>A depot login only sees its own depot. A company login sees every depot.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Name *</Label><Input data-testid="login-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Geelong office" /></div>
          <div className="space-y-2"><Label>Email {isNew && "*"}</Label><Input data-testid="login-email-input" type="email" value={form.email} disabled={!isNew} onChange={(e) => set("email", e.target.value)} placeholder="geelong@yourcompany.com.au" /></div>
          <div className="space-y-2"><Label>Access {isNew && "*"}</Label>
            <AccessSelect value={form.access} onChange={(v) => set("access", v)} disabled={locked} testId="login-access-select" />
            {locked && <p className="text-xs text-muted-foreground">This login always keeps company access.</p>}</div>
          <div className="space-y-2"><Label>{isNew ? "Password *" : "New password"}</Label>
            <Input data-testid="login-password-input" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={isNew ? "At least 6 characters" : "Leave blank to keep the current one"} /></div>
        </div>
        <DialogFooter>
          <Button data-testid="login-save-btn" onClick={save} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{isNew ? "Create login" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LoginRow = ({ l, onEdit, onRemove }) => (
  <li className="flex items-center gap-3 px-4 py-3" data-testid={`login-row-${l.id}`}>
    <span className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0 text-xs font-bold font-mono">{(l.name || l.email).slice(0, 2).toUpperCase()}</span>
    <div className="min-w-0 flex-1">
      <p className="font-semibold truncate">{l.name || l.email}{l.is_you && <span className="ml-1.5 text-xs font-normal text-muted-foreground">(you)</span>}</p>
      <p className="text-xs text-muted-foreground truncate">{l.email}</p>
    </div>
    <span data-testid={`login-access-${l.id}`} className={`hidden sm:inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${l.depot_id ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
      {l.depot_id ? <><MapPin className="w-3 h-3" /> {l.depot_name}</> : <><Layers className="w-3 h-3" /> All depots</>}
    </span>
    <Button size="icon" variant="ghost" className="rounded-full shrink-0" data-testid={`login-edit-${l.id}`} onClick={onEdit}><Pencil className="w-4 h-4" /></Button>
    {!l.is_you && !l.is_founder && (
      <Button size="icon" variant="ghost" className="rounded-full shrink-0 text-destructive hover:bg-destructive/10" data-testid={`login-remove-${l.id}`} onClick={onRemove}><Trash2 className="w-4 h-4" /></Button>
    )}
  </li>
);

export const LoginsCard = () => {
  const { depots, reload } = useDepot();
  const [logins, setLogins] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => api.logins().then(setLogins).catch(() => toast.error("Could not load logins"));
  useEffect(() => { load(); }, [depots.length]);

  const remove = async (l) => {
    if (!window.confirm(`Remove the login for ${l.email}? They won't be able to sign in any more.`)) return;
    try { await api.deleteLogin(l.id); toast.success("Login removed"); load(); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove login"); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6" data-testid="logins-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Supervisor logins</h3>
          <p className="text-sm text-muted-foreground">Who can sign in to this dashboard, and what they can see.</p>
        </div>
        <Button data-testid="login-add-btn" onClick={() => setDialog({})} className="rounded-full bg-primary hover:bg-primary/90 text-white shrink-0"><Plus className="w-4 h-4 mr-1.5" /> Add login</Button>
      </div>
      <ul className="divide-y divide-border border border-border rounded-xl overflow-hidden">
        {logins.map((l) => <LoginRow key={l.id} l={l} onEdit={() => setDialog(l)} onRemove={() => remove(l)} />)}
      </ul>
      {dialog && <LoginDialog login={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); reload(); }} />}
    </div>
  );
};
